/**
 * Demo Data Seeding Hook
 * 
 * React hook that automatically seeds demo observation data
 * for new users when they first log in to the MiEROS demo portal.
 * 
 * This ensures demo users have populated health data to visualize.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fhirObservationService } from '../services/FhirObservationService';
import Logger from './Logger';

// Local storage key to track if demo data has been seeded
const DEMO_DATA_SEEDED_KEY = 'mieDemoDataSeeded';

// Default seeding options for demo observations
const DEFAULT_SEEDING_OPTIONS = {
  daysOfData: 30,
  observationsPerDay: 1,
  observationTypes: [
    'HEART_RATE',
    'BODY_TEMPERATURE', 
    'OXYGEN_SATURATION',
    'BLOOD_GLUCOSE',
    'SYSTOLIC_BP',
    'DIASTOLIC_BP',
    'BODY_WEIGHT'
  ]
};

/**
 * Hook to manage demo data seeding for a user
 * @param {Object} authUser - Firebase auth user object
 * @param {Object} options - Optional seeding configuration
 * @returns {Object} Seeding state and control functions
 */
export const useDemoDataSeeding = (authUser, options = {}) => {
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingComplete, setSeedingComplete] = useState(false);
  const [seedingError, setSeedingError] = useState(null);
  const [seedingResult, setSeedingResult] = useState(null);
  
  // Track if seeding has been attempted to prevent duplicate calls
  const seedingAttempted = useRef(false);

  const seedingOptions = {
    ...DEFAULT_SEEDING_OPTIONS,
    ...options
  };

  /**
   * Check if demo data has already been seeded for this user
   * @returns {boolean} True if already seeded
   */
  const isDemoDataSeeded = useCallback(() => {
    if (!authUser?.uid) return false;
    
    try {
      const seedingRecord = localStorage.getItem(DEMO_DATA_SEEDED_KEY);
      if (!seedingRecord) return false;
      
      const record = JSON.parse(seedingRecord);
      return record[authUser.uid] === true;
    } catch (error) {
      Logger.warn('Error checking demo data seeding status:', error);
      return false;
    }
  }, [authUser?.uid]);

  /**
   * Mark demo data as seeded for this user
   */
  const markDemoDataSeeded = useCallback(() => {
    if (!authUser?.uid) return;
    
    try {
      const seedingRecord = JSON.parse(localStorage.getItem(DEMO_DATA_SEEDED_KEY) || '{}');
      seedingRecord[authUser.uid] = true;
      seedingRecord[`${authUser.uid}_timestamp`] = new Date().toISOString();
      localStorage.setItem(DEMO_DATA_SEEDED_KEY, JSON.stringify(seedingRecord));
    } catch (error) {
      Logger.warn('Error marking demo data as seeded:', error);
    }
  }, [authUser?.uid]);

  /**
   * Seed demo observations for the current user
   * @param {boolean} force - Force seeding even if already done
   */
  const seedDemoData = useCallback(async (force = false) => {
    if (!authUser?.uid) {
      Logger.debug('No auth user, skipping demo data seeding');
      return { skipped: true, reason: 'No authenticated user' };
    }

    // Check if already seeded (unless force is true)
    if (!force && isDemoDataSeeded()) {
      Logger.debug('Demo data already seeded for user:', authUser.uid);
      setSeedingComplete(true);
      return { skipped: true, reason: 'Already seeded' };
    }

    // Prevent duplicate seeding attempts in the same session
    if (seedingAttempted.current && !force) {
      Logger.debug('Seeding already attempted in this session');
      return { skipped: true, reason: 'Already attempted' };
    }

    seedingAttempted.current = true;
    setIsSeeding(true);
    setSeedingError(null);

    try {
      Logger.info('Starting demo observation seeding for user:', authUser.uid);

      const result = await fhirObservationService.seedDemoObservations(
        authUser,
        seedingOptions
      );

      if (result.skipped) {
        Logger.debug('Demo data seeding skipped - data already exists');
        markDemoDataSeeded();
        setSeedingComplete(true);
        setSeedingResult(result);
        return result;
      }

      if (result.success) {
        Logger.info(`Demo data seeding complete: ${result.createdCount} observations created`);
        markDemoDataSeeded();
        setSeedingComplete(true);
        setSeedingResult(result);
        return result;
      }

      return result;
    } catch (error) {
      Logger.error('Error seeding demo data:', error);
      setSeedingError(error.message || 'Failed to seed demo data');
      return { error: true, message: error.message };
    } finally {
      setIsSeeding(false);
    }
  }, [authUser, isDemoDataSeeded, markDemoDataSeeded, seedingOptions]);

  /**
   * Clear demo data seeding status (for testing/reset)
   */
  const clearSeedingStatus = useCallback(() => {
    if (!authUser?.uid) return;
    
    try {
      const seedingRecord = JSON.parse(localStorage.getItem(DEMO_DATA_SEEDED_KEY) || '{}');
      delete seedingRecord[authUser.uid];
      delete seedingRecord[`${authUser.uid}_timestamp`];
      localStorage.setItem(DEMO_DATA_SEEDED_KEY, JSON.stringify(seedingRecord));
      setSeedingComplete(false);
      setSeedingResult(null);
      seedingAttempted.current = false;
    } catch (error) {
      Logger.warn('Error clearing seeding status:', error);
    }
  }, [authUser?.uid]);

  /**
   * Clear all demo observations and reset seeding status
   */
  const resetDemoData = useCallback(async () => {
    if (!authUser?.uid) return { error: true, message: 'No authenticated user' };

    setIsSeeding(true);
    setSeedingError(null);

    try {
      // Delete all existing observations
      const deletedCount = await fhirObservationService.clearDemoObservations(authUser);
      Logger.info(`Cleared ${deletedCount} demo observations`);

      // Clear seeding status
      clearSeedingStatus();

      // Re-seed new demo data
      const result = await seedDemoData(true);
      
      return {
        success: true,
        deletedCount,
        newDataResult: result
      };
    } catch (error) {
      Logger.error('Error resetting demo data:', error);
      setSeedingError(error.message);
      return { error: true, message: error.message };
    } finally {
      setIsSeeding(false);
    }
  }, [authUser, clearSeedingStatus, seedDemoData]);

  // Auto-seed on mount if user is authenticated and not already seeded
  useEffect(() => {
    if (authUser?.uid && !isDemoDataSeeded() && !seedingAttempted.current) {
      // Small delay to ensure auth is fully settled
      const timeoutId = setTimeout(() => {
        seedDemoData();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [authUser?.uid, isDemoDataSeeded, seedDemoData]);

  return {
    // State
    isSeeding,
    seedingComplete,
    seedingError,
    seedingResult,
    
    // Functions
    seedDemoData,
    clearSeedingStatus,
    resetDemoData,
    isDemoDataSeeded: isDemoDataSeeded()
  };
};

/**
 * Standalone function to seed demo data (for use outside React components)
 * @param {Object} authUser - Firebase auth user object
 * @param {Object} options - Seeding options
 * @returns {Promise<Object>} Seeding result
 */
export const seedDemoObservationsForUser = async (authUser, options = {}) => {
  if (!authUser?.uid) {
    Logger.debug('No auth user provided for demo data seeding');
    return { skipped: true, reason: 'No authenticated user' };
  }

  const seedingOptions = {
    ...DEFAULT_SEEDING_OPTIONS,
    ...options
  };

  try {
    // Check if already seeded
    const seedingRecord = JSON.parse(localStorage.getItem(DEMO_DATA_SEEDED_KEY) || '{}');
    if (seedingRecord[authUser.uid] && !options.force) {
      Logger.debug('Demo data already seeded for user:', authUser.uid);
      return { skipped: true, reason: 'Already seeded' };
    }

    Logger.info('Starting demo observation seeding for user:', authUser.uid);
    
    const result = await fhirObservationService.seedDemoObservations(
      authUser,
      seedingOptions
    );

    // Mark as seeded
    if (result.success || result.skipped) {
      seedingRecord[authUser.uid] = true;
      seedingRecord[`${authUser.uid}_timestamp`] = new Date().toISOString();
      localStorage.setItem(DEMO_DATA_SEEDED_KEY, JSON.stringify(seedingRecord));
    }

    return result;
  } catch (error) {
    Logger.error('Error seeding demo observations:', error);
    return { error: true, message: error.message };
  }
};

export default useDemoDataSeeding;
